import { RefObject, useEffect, useState } from 'react';

/**
 * The touch-device stand-in for :hover.
 *
 * Everything interactive here was built around hover, which means none of it
 * ever ran on a phone: previews stayed frozen, words never filled in, rows
 * never moved. Hover is the wrong input on touch, but attention is a fair
 * substitute, so this reports whether an element currently sits in the band of
 * the viewport the reader is looking at.
 *
 * Returns false forever on hover-capable pointers, so desktop keeps real
 * hover and there is one behaviour rather than two that can drift apart. It
 * also stays false for anyone who asked for reduced motion.
 *
 * @param rootMargin the band. Vertical by default; pass a horizontal one for
 *                   things that travel sideways, like the marquee.
 */
export const useTouchActive = (
  ref: RefObject<HTMLElement | null>,
  rootMargin = '-35% 0px -35% 0px'
) => {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    if (!window.matchMedia('(hover: none)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const io = new IntersectionObserver(([entry]) => setActive(entry.isIntersecting), {
      rootMargin,
    });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin]);

  return active;
};

export default useTouchActive;
